import * as React from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";
import {
  UpdatePasswordMutationMutation,
  UpdatePasswordMutationMutationVariables
} from "../../../../schemaTypes";
import { normalizeErrors } from "../../../../utils/normalizeErrors";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";

const mapUpdatePasswordApolloError = (message: string): NormalizedErrorMap => {
  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes("current password") || normalizedMessage.includes("old password")) {
    return { oldPassword: message };
  }

  if (normalizedMessage.includes("confirm") || normalizedMessage.includes("does not match")) {
    return { confirmNewPassword: message };
  }

  if (normalizedMessage.includes("new password") || normalizedMessage.includes("password")) {
    return { newPassword: message };
  }

  return { general: message };
};

const UPDATE_PASSWORD_MUTATION = gql`
  mutation UpdatePasswordMutation(
    $oldPassword: String!
    $newPassword: String!
    $confirmNewPassword: String!
  ) {
    updatePassword(
      oldPassword: $oldPassword,
      newPassword: $newPassword,
      confirmNewPassword: $confirmNewPassword
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  children: (
    data: {
      submit: (
        values: UpdatePasswordMutationMutationVariables
      ) => Promise<NormalizedErrorMap | null>;
    loading: boolean,
    error: any,
    message: string | null,
    }
  ) => React.ReactElement | null;
}

export const UpdatePasswordController: React.FC<Props> = ({ children }) => {
  const [mutate, { loading, error, data }] = useMutation<
    UpdatePasswordMutationMutation,
    UpdatePasswordMutationMutationVariables
  >(UPDATE_PASSWORD_MUTATION);

  const submit = async (values: UpdatePasswordMutationMutationVariables) => {
    try {
      const { data: { updatePassword } = {} as any }: any = await mutate({ variables: values });

      if (updatePassword?.errors) {
        return normalizeErrors(updatePassword.errors);
      }

      if (!updatePassword) {
        return { general: "An unknown error occurred" };
      }

      return null;
    } catch (mutationError) {
      const apolloError = mutationError as ApolloError;
      const message =
        apolloError.graphQLErrors?.[0]?.message ||
        apolloError.message ||
        "An error occurred";

      return mapUpdatePasswordApolloError(message);
    }
  };

  const message = data?.updatePassword?.message || null;

  return children({
    submit,
    loading,
    error,
    message,
  });
};